import { useCallback, useEffect, useRef, useState } from 'react';
import { SaveStatus } from '../ui/feedback';

/**
 * Default lifetime of the "Saved" flash. Long enough that a user can
 * register the confirmation, short enough that the ticker doesn't hang
 * around competing with new interactions. Matches the checkmark-flash
 * timing on most autosave-driven surfaces.
 */
const DEFAULT_SAVED_DURATION_MS = 2500;

export interface UseAutosaveStatusOptions {
  /**
   * True while a save is in flight. Typically `mutation.isPending`
   * from a React Query mutation. Takes precedence over the Saved flash
   * so the spinner replaces the check when a new save fires mid-flash.
   */
  readonly isPending: boolean;
  /**
   * True when the most recent save attempt failed. Typically
   * `mutation.isError`. Suppresses the Saved flash — pair with a
   * MessageBanner in the page for the actual error copy.
   */
  readonly isError: boolean;
  /**
   * How long to render the Saved state before returning to Idle.
   * Defaults to {@link DEFAULT_SAVED_DURATION_MS} (2500ms).
   */
  readonly savedDurationMs?: number;
}

export interface UseAutosaveStatusResult {
  /**
   * The current status to feed a `SaveIndicator`. Precedence:
   * Saving > Error > Saved > Idle.
   */
  readonly saveStatus: SaveStatus;
  /**
   * Flash the Saved state. Call this from the mutation's `onSuccess`
   * and from any local persistence path (localStorage writes,
   * IndexedDB, etc.) so the indicator reflects both server-side and
   * client-side saves through the same surface.
   *
   * Referentially stable — safe to place in `useCallback`/`useMemo`
   * dependency arrays without re-scheduling their side effects.
   */
  readonly markSaved: () => void;
}

/**
 * Transient "Saved" indicator state for autosaved forms.
 *
 * Solves two problems the naive derivation doesn't:
 *   1. `mutation.isSuccess` stays true until the next mutation, so a
 *      status derived from it renders "Saved" forever. This hook
 *      dismisses the flash after {@link UseAutosaveStatusOptions.savedDurationMs}.
 *   2. Not every save goes through a mutation — client-only writes
 *      (localStorage preferences, IndexedDB drafts) need the same
 *      confirmation. `markSaved()` lets those paths reuse the same
 *      indicator without inventing a parallel status.
 *
 * The hook only produces `SaveStatus` values; it doesn't know about the
 * `SaveIndicator` component. Pair it with `useDebouncedAutosave` for the
 * mutation side and call `markSaved()` from any other persistence path.
 *
 * @example
 *   const mutation = useUpdateSomethingMutation();
 *   const { saveStatus, markSaved } = useAutosaveStatus({
 *     isPending: mutation.isPending,
 *     isError: mutation.isError,
 *   });
 *
 *   const handleSave = useCallback((next) => {
 *     mutation.mutate(next, { onSuccess: markSaved });
 *   }, [mutation, markSaved]);
 *
 *   const handleLocalPreferenceChange = (value) => {
 *     writeToLocalStorage(value);
 *     markSaved();
 *   };
 */
export function useAutosaveStatus({
  isPending,
  isError,
  savedDurationMs = DEFAULT_SAVED_DURATION_MS,
}: UseAutosaveStatusOptions): UseAutosaveStatusResult {
  const [isSavedFlashing, setIsSavedFlashing] = useState<boolean>(false);
  // Browser `setTimeout` returns `number`. Explicit primitive type
  // instead of `ReturnType<typeof setTimeout>` — v2's lint bans the
  // `ReturnType` utility because it hides the intent (the codebase
  // targets the DOM, not Node, so the handle is always numeric).
  const timerRef = useRef<number | null>(null);

  // Central timer cleanup — used by both a fresh `markSaved` (to restart
  // the countdown) and by the unmount effect (so a stale timer doesn't
  // fire setState on a gone component).
  const clearTimer = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const markSaved = useCallback((): void => {
    clearTimer();
    setIsSavedFlashing(true);
    // Use `window.setTimeout` (not bare `setTimeout`) so TypeScript
    // resolves to the DOM's numeric-handle overload. Bare `setTimeout`
    // picks up Node's `Timeout` return type when `@types/node` is
    // ambient, which conflicts with the numeric `timerRef` above.
    timerRef.current = window.setTimeout(() => {
      setIsSavedFlashing(false);
      timerRef.current = null;
    }, savedDurationMs);
  }, [clearTimer, savedDurationMs]);

  // Cleanup only — no set-state-in-effect. The interruption of the Saved
  // flash while `isPending`/`isError` are active is handled below in the
  // derivation via precedence, not by mutating state from an effect.
  useEffect(() => clearTimer, [clearTimer]);

  // Precedence resolves the state derivation without needing a reset
  // effect: an in-flight save or a fresh error visually replaces the
  // Saved flash, and when they clear the flash resumes if its timer is
  // still running.
  let saveStatus: SaveStatus = SaveStatus.Idle;
  if (isPending) {
    saveStatus = SaveStatus.Saving;
  } else if (isError) {
    saveStatus = SaveStatus.Error;
  } else if (isSavedFlashing) {
    saveStatus = SaveStatus.Saved;
  }

  return { saveStatus, markSaved };
}
